import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Coins, Gift, Sparkles, Clock, ShoppingBag, Zap, HelpCircle } from 'lucide-react';

const HowCoinsWorkPage = () => {
  const navigate = useNavigate();
  const [expandedFaq, setExpandedFaq] = useState(null);

  const earnMethods = [
    {
      icon: <Coins className="w-6 h-6" />,
      title: "Daily Login Bonus",
      description: "Claim 10 coins every day just for opening the app",
      coins: "+10",
      color: "from-yellow-500 to-orange-500"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Login Streak Bonus",
      description: "Keep your streak going for extra coins (up to +20 bonus)",
      coins: "+5 to +20",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: <Gift className="w-6 h-6" />,
      title: "Receive Gifts",
      description: "When someone sends you a gift, you receive coins",
      coins: "Varies",
      color: "from-pink-500 to-red-500"
    }
  ];

  const spendMethods = [
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "Super Likes",
      description: "Stand out and show extra interest in a match",
      cost: "50 coins",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: <Gift className="w-6 h-6" />,
      title: "Send Gifts",
      description: "Send virtual gifts to express yourself",
      cost: "15-250 coins",
      color: "from-pink-500 to-rose-500"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Profile Boost",
      description: "Get more visibility for 30 minutes",
      cost: "100 coins",
      color: "from-amber-500 to-orange-500"
    }
  ];

  const coinPacks = [
    { name: "Starter", coins: 100, price: "$1.99", bonus: null, popular: false },
    { name: "Popular", coins: 550, price: "$7.99", bonus: "+50 free", popular: true },
    { name: "Best Value", coins: 1400, price: "$14.99", bonus: "+200 free", popular: false }
  ];

  const faqs = [
    {
      question: "Do coins expire?",
      answer: "Yes, earned coins (from daily login, streaks, etc.) expire after 60 days. However, purchased coins NEVER expire! We'll warn you when coins are about to expire."
    },
    {
      question: "Which coins are used first?",
      answer: "We use the oldest coins first (FIFO - First In, First Out). This means your expiring coins get used before your purchased coins, so nothing goes to waste."
    },
    {
      question: "Can I transfer coins to another user?",
      answer: "You can't transfer coins directly, but you can send gifts! When you send a gift, the recipient receives a portion of the coin value."
    },
    {
      question: "What happens if I don't use my coins?",
      answer: "Earned coins expire after 60 days if unused. Purchased coins stay in your account forever. We recommend using earned coins for daily interactions!"
    },
    {
      question: "How do I get more coins?",
      answer: "Log in daily for free coins, maintain your streak for bonuses, or purchase coin packs for instant coins that never expire."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1C3A7A] to-[#0f2347]">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#1C3A7A]/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
          <h1 className="text-xl font-bold text-white ml-2">How Coins Work</h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-8">
        {/* Hero Section */}
        <div className="text-center py-6">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center shadow-lg shadow-orange-500/30">
            <Coins className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">RecoveryMatch Coins</h2>
          <p className="text-gray-300">Your currency for standing out and expressing yourself</p>
        </div>

        {/* Earn Section */}
        <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <span className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
              <span className="text-green-400">+</span>
            </span>
            Ways to Earn Coins
          </h3>
          <div className="space-y-4">
            {earnMethods.map((method, index) => (
              <div key={index} className="flex items-center gap-4 p-3 bg-white/5 rounded-xl">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${method.color} flex items-center justify-center text-white`}>
                  {method.icon}
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-white">{method.title}</h4>
                  <p className="text-sm text-gray-400">{method.description}</p>
                </div>
                <div className="text-green-400 font-semibold">{method.coins}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Spend Section */}
        <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <span className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center">
              <ShoppingBag className="w-4 h-4 text-orange-400" />
            </span>
            Ways to Spend Coins
          </h3>
          <div className="space-y-4">
            {spendMethods.map((method, index) => (
              <div key={index} className="flex items-center gap-4 p-3 bg-white/5 rounded-xl">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${method.color} flex items-center justify-center text-white`}>
                  {method.icon}
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-white">{method.title}</h4>
                  <p className="text-sm text-gray-400">{method.description}</p>
                </div>
                <div className="text-orange-400 font-semibold">{method.cost}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Expiration Notice */}
        <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-2xl p-6 border border-amber-500/30">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
              <Clock className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h3 className="font-semibold text-white mb-2">About Coin Expiration</h3>
              <ul className="space-y-2 text-sm text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-amber-400 mt-0.5">•</span>
                  <span><strong className="text-white">Earned coins</strong> (daily bonus, streaks) expire after <strong className="text-amber-400">60 days</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400 mt-0.5">•</span>
                  <span><strong className="text-white">Purchased coins</strong> <strong className="text-green-400">NEVER expire</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 mt-0.5">•</span>
                  <span>Oldest coins are always used first (FIFO)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-400 mt-0.5">•</span>
                  <span>We'll notify you when coins are about to expire</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Coin Packs */}
        <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <span className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
              <ShoppingBag className="w-4 h-4 text-purple-400" />
            </span>
            Coin Packs
          </h3>
          <div className="grid gap-3">
            {coinPacks.map((pack, index) => (
              <div 
                key={index} 
                className={`flex items-center justify-between p-4 rounded-xl ${
                  pack.popular 
                    ? 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30' 
                    : 'bg-white/5'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center">
                    <Coins className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-white">{pack.coins} coins</span>
                      {pack.popular && (
                        <span className="text-xs px-2 py-0.5 bg-purple-500 rounded-full text-white">Popular</span>
                      )}
                    </div>
                    {pack.bonus && (
                      <span className="text-sm text-green-400">{pack.bonus}</span>
                    )}
                  </div>
                </div>
                <div className="text-white font-semibold">{pack.price}</div>
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-400 mt-4 text-center">
            Purchased coins never expire!
          </p>
        </div>

        {/* FAQ Section */}
        <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <span className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
              <HelpCircle className="w-4 h-4 text-blue-400" />
            </span>
            Frequently Asked Questions
          </h3>
          <div className="space-y-2">
            {faqs.map((faq, index) => (
              <div key={index} className="border-b border-white/10 last:border-0">
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full py-4 flex items-center justify-between text-left"
                >
                  <span className="font-medium text-white pr-4">{faq.question}</span>
                  <ChevronLeft 
                    className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${
                      expandedFaq === index ? 'rotate-90' : '-rotate-90'
                    }`} 
                  />
                </button>
                {expandedFaq === index && (
                  <p className="pb-4 text-gray-300 text-sm">{faq.answer}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center pb-8">
          <button
            onClick={() => navigate('/gift-shop')}
            className="px-8 py-3 bg-gradient-to-r from-[#FAB710] to-[#f59e0b] text-[#1C3A7A] font-semibold rounded-full hover:shadow-lg hover:shadow-[#FAB710]/30 transition-all"
          >
            Go to Gift Shop
          </button>
        </div>
      </div>
    </div>
  );
};

export default HowCoinsWorkPage;
